import React, { useEffect, useMemo } from "react";

const TEAL = "var(--primary-theme-color)";
const BORDER = "1px solid var(--primary-popup-stroke-color)";
const TEXT = "var(--primary-text-color)";
const TEXT_MUTED = "var(--primary-text-color-80)";
const LINK = "var(--primary-text-theme-color)";

function reportDisplayName(details) {
    if (!details || typeof details !== "object") return "Report";
    const d = details;
    if (d.report_name) return String(d.report_name);
    if (d.name) return String(d.name);
    if (d.file_name) return String(d.file_name);
    if (d.filename) return String(d.filename);
    if (d.title) return String(d.title);
    if (d.report_id != null) return `Report ${d.report_id}`;
    return "Report";
}

export default function InlineSuccessAlert({
    alertDetails,
    setReportGeneratedSuccessAlert,
    handleViewClick,
}) {
    const isCompleted = alertDetails?.status === "completed";

    useEffect(() => {
        const timeoutId = setTimeout(() => {
            setReportGeneratedSuccessAlert({
                showAlert: false,
                alertDetails: {},
            });
        }, 15000);

        return () => clearTimeout(timeoutId);
    }, [setReportGeneratedSuccessAlert]);

    const dismiss = () =>
        setReportGeneratedSuccessAlert({
            showAlert: false,
            alertDetails: {},
        });

    const reportLabel = useMemo(
        () => reportDisplayName(alertDetails),
        [alertDetails],
    );

    const timeStr = useMemo(
        () =>
            new Date().toLocaleTimeString(undefined, {
                hour: "numeric",
                minute: "2-digit",
                second: "2-digit",
                hour12: true,
            }),
        [],
    );

    return (
        <div
            style={{
                position: "fixed",
                top: 53,
                right: 4,
                zIndex: 1400,
                maxWidth: 420,
                width: "min(420px, calc(100vw - 48px))",
                backgroundColor: "var(--primary-popup-background-color)",
                borderRadius: 8,
                border: BORDER,
                boxShadow: "0 8px 32px rgba(0, 0, 0, 0.45)",
                padding: "15px 15px 15px",
                fontFamily: "Poppins, sans-serif",
            }}
            role="alert"
        >
            {/* Header */}
            <div
                style={{
                    position: "relative",
                    marginBottom: 16,
                    paddingRight: 28,
                }}
            >
                <div
                    style={{
                        textAlign: "center",
                        fontWeight: 600,
                        fontSize: 16,
                        color: TEXT,
                    }}
                >
                    Notification
                </div>
                <button
                    type="button"
                    onClick={dismiss}
                    aria-label="Close notification"
                    style={{
                        position: "absolute",
                        top: -4,
                        right: 0,
                        width: 32,
                        height: 32,
                        border: "none",
                        background: "transparent",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: "var(--primary-text-color)",
                        fontSize: 20,
                        lineHeight: 1,
                        padding: 0,
                    }}
                >
                    ×
                </button>
            </div>

            {/* Body */}
            <div
                style={{
                    display: "flex",
                    gap: 14,
                    alignItems: "flex-start",
                }}
            >
                <div
                    style={{
                        flexShrink: 0,
                        width: 22,
                        height: 22,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        color: TEAL,
                    }}
                    aria-hidden
                >
                    <div
                        style={{
                            width: 22,
                            height: 22,
                            borderRadius: "50%",
                            backgroundColor: TEAL,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            color: "var(--primary-button-text-color)",
                        }}
                    >
                        <svg
                            width="11"
                            height="11"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2.5"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M20 6L9 17l-5-5" />
                        </svg>
                    </div>
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <p
                        style={{
                            margin: 0,
                            fontSize: 14,
                            lineHeight: 1.45,
                            color: TEXT,
                            fontFamily: "Poppins, sans-serif",
                        }}
                    >
                        <span
                            style={{
                                color: LINK,
                                textDecoration: "underline",
                                textUnderlineOffset: 2,
                                cursor: "pointer",
                            }}
                        >
                            {reportLabel}
                        </span>{" "}
                        {isCompleted
                            ? "has been completed."
                            : "has been generated."}
                    </p>
                    <div
                        style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            marginTop: 14,
                            gap: 12,
                        }}
                    >
                        <button
                            onClick={() => {
                                handleViewClick(alertDetails);
                                dismiss();
                            }}
                            type="button"
                            style={{
                                border: "none",
                                background: "none",
                                padding: 0,
                                cursor: "pointer",
                                fontFamily: "Poppins, sans-serif",
                                fontSize: 12,
                                fontWeight: 500,
                                color: LINK,
                                textDecoration: "underline",
                                textUnderlineOffset: 2,
                            }}
                        >
                            Check now
                        </button>
                        <span
                            style={{
                                fontSize: 12,
                                color: TEXT_MUTED,
                                whiteSpace: "nowrap",
                                fontFamily: "Poppins, sans-serif",
                            }}
                        >
                            {timeStr}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}
